import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle, XCircle, Search, Clock, Award, User, Building, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Badge from '@/components/Badge';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDate(d) {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return '—'; }
}

export default function VerifyPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [certId, setCertId] = useState(searchParams.get('id') || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const id = searchParams.get('id');
    if (id) {
      setCertId(id);
      doVerify(id);
    }
  }, [searchParams]);

  const doVerify = async (id) => {
    const lookupId = id || certId;
    if (!lookupId.trim()) return;
    setLoading(true);
    setError(null);
    setResult(null);
    setSearched(true);
    try {
      const res = await axios.get(`${API}/api/verify/${encodeURIComponent(lookupId.trim())}`);
      setResult(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'not_found' : 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    doVerify();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-[#002868]">
        <div className="max-w-3xl mx-auto px-6 py-6">
          <div className="flex items-center gap-3 mb-4">
            <Badge className="w-8 h-8" />
            <button onClick={() => navigate('/')} className="text-white/70 hover:text-white text-sm font-medium transition-colors">
              Colorado Bail Enforcement Agency LLC
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Shield className="w-7 h-7 text-amber-400" />
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Barlow Condensed' }}>
              AGENT VERIFICATION
            </h1>
          </div>
          <p className="text-blue-200 text-sm mt-2 max-w-lg">
            Verify the certification status of a Colorado Bail Enforcement Agent.
            Enter the Agent ID found on the front of the agent's identification card.
          </p>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10">
        {/* Search form */}
        <form onSubmit={handleSubmit} className="mb-8">
          <label className="text-sm font-semibold text-gray-700 mb-2 block">Agent ID / Certificate Number</label>
          <div className="flex gap-3">
            <input
              type="text"
              value={certId}
              onChange={(e) => setCertId(e.target.value)}
              placeholder="e.g. CBE-2026-00001"
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg text-base font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none"
              data-testid="verify-input"
            />
            <Button
              type="submit"
              disabled={loading || !certId.trim()}
              className="bg-[#002868] hover:bg-blue-900 text-white font-semibold px-6"
              data-testid="verify-submit-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <><Search className="w-4 h-4 mr-2" /> Verify</>
              )}
            </Button>
          </div>
        </form>

        {/* Results */}
        {searched && !loading && result && (
          <div className="bg-white border-2 border-green-200 rounded-xl overflow-hidden shadow-md" data-testid="verify-result">
            {/* Status banner */}
            <div className={`px-6 py-4 flex items-center gap-3 ${
              result.status === 'Active'
                ? 'bg-green-50 border-b border-green-200'
                : 'bg-red-50 border-b border-red-200'
            }`}>
              {result.status === 'Active' ? (
                <CheckCircle className="w-6 h-6 text-green-600" />
              ) : (
                <XCircle className="w-6 h-6 text-red-600" />
              )}
              <div>
                <p className={`font-bold text-lg ${result.status === 'Active' ? 'text-green-800' : 'text-red-800'}`}>
                  {result.status === 'Active' ? 'VERIFIED — Active Agent' : 'EXPIRED — Certification Expired'}
                </p>
                <p className="text-sm text-gray-500">
                  {result.status === 'Active'
                    ? 'This agent holds a valid certification'
                    : 'This agent\'s certification has expired and requires renewal'}
                </p>
              </div>
            </div>

            {/* Agent details with photo */}
            <div className="p-6">
              <div className="flex gap-6">
                {/* Photo */}
                <div className="shrink-0">
                  {result.photo ? (
                    <img
                      src={result.photo}
                      alt={result.full_name}
                      className="object-cover border-2 border-gray-200"
                      style={{ width: 110, height: 140, borderRadius: 6 }}
                      data-testid="verify-agent-photo"
                    />
                  ) : (
                    <div
                      className="flex flex-col items-center justify-center bg-gray-100 border-2 border-gray-200 text-gray-400"
                      style={{ width: 110, height: 140, borderRadius: 6 }}
                    >
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 20c0-4 4-7 8-7s8 3 8 7" />
                      </svg>
                      <span className="text-[10px] mt-1">No Photo</span>
                    </div>
                  )}
                </div>

                {/* Details */}
                <div className="space-y-3 flex-1">
                  <DetailRow icon={User} label="Agent Name" value={result.full_name} />
                  <DetailRow icon={Award} label="Agent ID" value={result.certificate_number} mono />
                  <DetailRow icon={Shield} label="Certification" value={result.course} />
                  <DetailRow icon={Clock} label="Issued" value={formatDate(result.issue_date)} />
                  <DetailRow icon={Clock} label="Expires" value={formatDate(result.expiration_date)} />
                  <DetailRow icon={Building} label="Issuing Agency" value={result.issuing_agency} />
                </div>
              </div>
            </div>
          </div>
        )}

        {searched && !loading && error === 'not_found' && (
          <div className="bg-white border-2 border-amber-200 rounded-xl p-6 text-center" data-testid="verify-not-found">
            <XCircle className="w-10 h-10 text-amber-500 mx-auto mb-3" />
            <p className="font-bold text-gray-900 text-lg mb-1">No Record Found</p>
            <p className="text-gray-500 text-sm max-w-md mx-auto">
              No active certification was found for this Agent ID. Please double-check the
              number and try again, or contact Colorado BEA for assistance.
            </p>
          </div>
        )}

        {searched && !loading && error === 'error' && (
          <div className="bg-white border-2 border-red-200 rounded-xl p-6 text-center">
            <XCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
            <p className="font-bold text-gray-900 text-lg mb-1">Verification Error</p>
            <p className="text-gray-500 text-sm">Something went wrong. Please try again.</p>
          </div>
        )}

        {/* Law Enforcement Notice */}
        <div className="mt-8 bg-amber-50 border-2 border-amber-200 rounded-xl p-5" data-testid="law-enforcement-notice">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <h3 className="font-bold text-gray-900 text-sm mb-2">Notice to Law Enforcement</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                If you need a copy of this agent's identification card, contact information, or any
                additional documentation, please contact us directly:
              </p>
              <div className="mt-3 flex items-center gap-2">
                <Mail className="w-4 h-4 text-amber-600" />
                <a
                  href="mailto:gtafoya@coloradobea.org"
                  className="text-blue-700 font-semibold text-sm hover:underline"
                  data-testid="le-email-link"
                >
                  gtafoya@coloradobea.org
                </a>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                Colorado Bail Enforcement Agency LLC — Gilbert Tafoya, Director
              </p>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-5">
          <h3 className="font-bold text-gray-900 text-sm mb-2">About Agent Verification</h3>
          <p className="text-gray-600 text-sm leading-relaxed">
            Colorado Bail Enforcement Agency LLC issues Agent IDs to individuals who have
            successfully completed the Colorado Bail Enforcement Certification Course. Each
            agent is assigned a unique certification number that can be verified using this page.
            Certifications are valid for 2 years from the date of issue and require continuing
            education for renewal.
          </p>
          <div className="mt-3 pt-3 border-t border-blue-200">
            <p className="text-xs text-gray-500">
              For questions or concerns about a bail enforcement agent, contact the{' '}
              <span className="font-semibold">Colorado Division of Insurance</span> at{' '}
              303-894-7490 or 1-800-930-3745.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

const DetailRow = ({ icon: Icon, label, value, mono }) => (
  <div className="flex items-start gap-3">
    <Icon className="w-4 h-4 text-gray-400 mt-0.5 shrink-0" />
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{label}</p>
      <p className={`text-base font-semibold text-gray-900 ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
    </div>
  </div>
);
